import React, { useState, useEffect } from 'react';
import { GlobalHeader, Button } from '@ver-uds/react';
import { NavLink, useLocation } from 'react-router-dom';
import logo from '../images/save-logo.svg';
import { AuthState, AuthStates, useAuthState, useLogout } from '@vis-auth/react';

import { isToggleEnabled, getUserData, getAuthConfig, hasUserRole } from '@save/utils';
import { useChooseRouteConfig } from '../hooks/useChooseRouteConfig';
import { useTranslation } from 'react-i18next';
import { useSwitchLanguage } from '../localization/useSwitchLanguage';

const getDisplayName = (authState: AuthState, userData: any) => {
  // User Data is not populated for onboarding users bc they don't have correct access in the user-service
  // Once https://git.uscis.dhs.gov/USCIS/vis-user-service/pull/880 is deployed this can be removed
  if (hasUserRole(authState.user.roles, 'ONBOARDING')) {
    return 'Onboarding User';
  }

  return userData ? `${userData.firstName} ${userData.lastName}` : null;
};

const isCurrentPath = (group, pathname) => {
  if (group.headerCurrentPaths) return group.headerCurrentPaths.includes(pathname);
  if (group.routes) return pathname.includes(group.routes[0].url);
  return false;
};

const buildLink = ({ text, i18n, url, urlToggle, isExternal }, t) => {
  const finalText = i18n ? t(i18n) : text;
  const finalUrl = isToggleEnabled(urlToggle?.toggle) ? urlToggle?.url : url;

  return (
    <>
      {isExternal ? (
        <a href={finalUrl} data-testid="header-nav-link">
          {finalText}
        </a>
      ) : (
        <NavLink to={finalUrl} exact activeClassName={GlobalHeader.activeClass} data-testid="header-nav-link">
          {finalText}
        </NavLink>
      )}
    </>
  );
};

const buildLinks = (routes, t) => {
  return routes?.map((route) => {
    return buildLink(route, t);
  });
};

const buildNavItems = (groups, pathname: string, t) => {
  let menus = [];
  let visibleRoutes = [];
  groups?.forEach((group) => {
    if (!group.routes) return;

    visibleRoutes = group.routes?.filter((route) => !route.isHiddenInHeader);
    if (visibleRoutes.length == 1) {
      menus.push(buildLink(visibleRoutes[0], t));
    } else if (!group.isUserDropDown) {
      menus.push(
        <GlobalHeader.Menu
          id={'menu-' + group.groupName.toLowerCase()}
          title={group.groupName}
          current={isCurrentPath(group, pathname)}
          data-testid="header-nav-group"
        >
          {buildLinks(visibleRoutes, t)}
        </GlobalHeader.Menu>,
      );
    }
  });
  return <GlobalHeader.NavItems>{menus}</GlobalHeader.NavItems>;
};

export default function HeaderLinks({ routeConfig }) {
  const location = useLocation();
  const [pathname, setPathname] = useState(location.pathname);
  const logout = useLogout();
  const currentConfig = useChooseRouteConfig(routeConfig);

  const authState = useAuthState();
  const userData = getUserData();

  const { t } = useTranslation();
  useSwitchLanguage();

  useEffect(() => {
    setPathname(location.pathname);
  }, [location, authState]);

  const navGroups = currentConfig?.groups?.filter((group) => !group.isUserDropDown);

  navGroups?.sort(({ headerOrder: a }, { headerOrder: b }) => {
    return a - b;
  });

  const userDropdownRoutes = currentConfig?.groups
    ?.find((group) => group.isUserDropDown)
    ?.routes?.filter((route) => {
      if (route.visibleToggle != undefined) {
        return isToggleEnabled(route.visibleToggle);
      }
      return true;
    });

  return (
    <GlobalHeader
      logo={<img src={logo} alt="SAVE" />}
      navItems={buildNavItems(navGroups, pathname, t)}
      rightNavItems={
        currentConfig?.hideAuth ? null : AuthStates.authenticated === authState.state ? (
          <GlobalHeader.NavItems id="user-dropdown-menu">
            <GlobalHeader.Menu
              id="user-dropdown"
              title={getDisplayName(authState, userData)}
              icon="user"
              current={pathname.includes('/profile')}
              data-testid="user-dropdown"
            >
              {buildLinks(userDropdownRoutes, t)}
              <Button className="z-100" variant="destructive" onClick={logout}>
                {t('header.signOut')}
              </Button>
            </GlobalHeader.Menu>
          </GlobalHeader.NavItems>
        ) : (
          <GlobalHeader.NavItems>
            <a href={getAuthConfig().redirectUri}>{t('header.signIn')}</a>
          </GlobalHeader.NavItems>
        )
      }
    />
  );
}

export function ErrorHeader() {
  const { t } = useTranslation();
  return (
    <GlobalHeader
      logo={<img src={logo} alt="SAVE" />}
      rightNavItems={
        <GlobalHeader.NavItems>
          <a href={getAuthConfig().redirectUri}>{t('header.signIn')}</a>
        </GlobalHeader.NavItems>
      }
    />
  );
}
